import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Trash2, Mail, MailOpen } from 'lucide-react';
import { api } from '@/lib/api';
import type { ContactMessage } from '@inafana/shared';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { formatDateTime } from '@/lib/utils';

export function Messages(): React.JSX.Element {
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'messages'],
    queryFn: () => api<{ messages: ContactMessage[] }>('/admin/messages'),
  });

  const toggleRead = useMutation({
    mutationFn: ({ id, isRead }: { id: string; isRead: boolean }) =>
      api(`/admin/messages/${id}`, { method: 'PATCH', body: { isRead } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'messages'] }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api(`/admin/messages/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'messages'] }),
  });

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-semibold">Messages</h1>
        <p className="text-sm text-muted-foreground">Contact form submissions.</p>
      </header>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12" />
              <TableHead>From</TableHead>
              <TableHead>Subject</TableHead>
              <TableHead>Received</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground">
                  Loading…
                </TableCell>
              </TableRow>
            )}
            {!isLoading && (data?.messages.length ?? 0) === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground">
                  No messages yet.
                </TableCell>
              </TableRow>
            )}
            {data?.messages.map((m) => (
              <TableRow key={m.id} className={m.isRead ? '' : 'bg-accent/20'}>
                <TableCell>
                  {m.isRead ? (
                    <MailOpen className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Badge>new</Badge>
                  )}
                </TableCell>
                <TableCell>
                  <div className="font-medium">{m.name}</div>
                  <div className="text-xs text-muted-foreground">{m.email}</div>
                </TableCell>
                <TableCell>
                  <details>
                    <summary className="cursor-pointer">{m.subject}</summary>
                    <p className="mt-2 whitespace-pre-wrap text-sm text-muted-foreground">
                      {m.message}
                    </p>
                  </details>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {formatDateTime(m.createdAt)}
                </TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex gap-1">
                    <Button
                      size="icon"
                      variant="ghost"
                      title={m.isRead ? 'Mark unread' : 'Mark read'}
                      onClick={() => toggleRead.mutate({ id: m.id, isRead: !m.isRead })}
                      disabled={toggleRead.isPending}
                    >
                      <Mail className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      title="Delete"
                      onClick={() => {
                        if (confirm('Delete this message?')) remove.mutate(m.id);
                      }}
                      disabled={remove.isPending}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
